import {
  ToolStatusEvent,
  type JsonValue,
  type ErrorEvent,
} from '@packages/systems/ai/agent/shared';
import type {
  ClientFrameworkEvent,
  ClientTextEvent,
} from '@packages/domains/ai-assistant/data-types';
import {Message, ToolMessage, InterruptedMarkerMessage} from '../types';

function getIntentFromInput(input: JsonValue | undefined): string | undefined {
  if (
    input &&
    typeof input === 'object' &&
    input !== null &&
    'intent' in input
  ) {
    const intent = (input as {intent?: unknown}).intent;
    if (typeof intent === 'string' && intent.length > 0) return intent;
  }
  return undefined;
}

const isMatchingToolStatus = (
  firstTool: ToolStatusEvent,
  secondTool: ToolStatusEvent
) => {
  const sameCallOrMissingCallId =
    !firstTool.callId ||
    !secondTool.callId ||
    firstTool.callId === secondTool.callId;
  const sameIntent =
    getIntentFromInput(firstTool.input) ===
    getIntentFromInput(secondTool.input);

  return (
    firstTool.tool === secondTool.tool && sameCallOrMissingCallId && sameIntent
  );
};

const isCollapsibleToolStatus = (
  firstTool: ToolStatusEvent,
  secondTool: ToolStatusEvent
) => {
  // `ask-user-question` collapses only across events from the SAME call —
  // the executing → executed pair for one question must merge so the
  // spinner chip is replaced by the resolved chip, but two separate calls
  // (different callIds) must stay separate so each question + answer
  // remains visible in the transcript. Other tools collapse on tool + intent
  // and accumulate a count, which is the wrong shape for ask-user-question
  // because its merged output would silently drop user-visible content.
  // `ask-user-question` is behind a feature flag, none impacting for other
  // tools.
  if (
    firstTool.tool === 'ask-user-question' &&
    secondTool.tool === 'ask-user-question'
  ) {
    return Boolean(firstTool.callId) && firstTool.callId === secondTool.callId;
  }

  const sameIntent =
    getIntentFromInput(firstTool.input) ===
    getIntentFromInput(secondTool.input);

  return firstTool.tool === secondTool.tool && sameIntent;
};

const pruneInterruptedExecutingToolStatuses = (
  events: ClientFrameworkEvent[]
) => {
  if (!events.some((event) => event.type === 'interrupted')) return events;

  return events.filter((event, index) => {
    if (event.type !== 'tool_status' || event.status !== 'executing') {
      return true;
    }

    const laterEvents = events.slice(index + 1);
    const nextInterruptedIndex = laterEvents.findIndex(
      (candidate) => candidate.type === 'interrupted'
    );
    if (nextInterruptedIndex === -1) return true;

    const hasResolvedStatus = laterEvents
      .slice(0, nextInterruptedIndex)
      .some(
        (candidate) =>
          candidate.type === 'tool_status' &&
          (candidate.status === 'executed' || candidate.status === 'failed') &&
          isMatchingToolStatus(event, candidate)
      );

    return hasResolvedStatus;
  });
};

const pruneInterruptedAbortSignalErrors = (events: ClientFrameworkEvent[]) => {
  if (!events.some((event) => event.type === 'interrupted')) return events;

  return events.filter(
    (event) =>
      event.type !== 'error' || event.context?.operation !== 'abort_signal'
  );
};

const isDisplayableEvent = (event: ClientFrameworkEvent) => {
  return (
    event.type === 'text' ||
    event.type === 'tool_status' ||
    event.type === 'user_input' ||
    event.type === 'error' ||
    event.type === 'interrupted'
  );
};

const makeId = (event: ClientFrameworkEvent) => {
  // Use the framework event's unique id to avoid duplicate React keys
  return event.id;
};

const groupConsecutiveEvents = (events: ClientFrameworkEvent[]) => {
  const groups: ClientFrameworkEvent[][] = [];
  let currentGroup: ClientFrameworkEvent[] = [];
  events.forEach((event) => {
    if (currentGroup.length === 0) {
      // Start a new group for the first event
      currentGroup.push(event);
      return;
    }
    const lastEvent = currentGroup[currentGroup.length - 1];
    if (
      event.type === 'text' &&
      lastEvent?.type === 'text' &&
      event.category === lastEvent.category
    ) {
      // Group text events in the same category
      currentGroup.push(event);
    } else if (
      lastEvent?.type === 'tool_status' &&
      event.type === 'tool_status'
    ) {
      const prevTool = lastEvent as ToolStatusEvent;
      const nextTool = event as ToolStatusEvent;
      if (isCollapsibleToolStatus(prevTool, nextTool)) {
        // Same tool + intent; consecutive calls collapse into a single chip with count.
        currentGroup.push(event);
      } else {
        groups.push(currentGroup);
        currentGroup = [event];
      }
    } else if (
      lastEvent?.type === event.type &&
      event.type !== 'text' &&
      event.type !== 'tool_status'
    ) {
      // Group other consecutive events of the same type
      currentGroup.push(event);
    } else {
      // Start a new group for non-consecutive events
      groups.push(currentGroup);
      currentGroup = [event];
    }
  });
  if (currentGroup.length > 0) {
    // Push the last group
    groups.push(currentGroup);
  }
  return groups;
};

// Delete streaming text events that are followed by a final text event.
const pruneStreamingTextGroups = (groups: ClientFrameworkEvent[][]) => {
  // Remove streaming text groups that are followed by a final text group
  return groups.filter((group, index, array) => {
    if (group[0]?.type !== 'text') return true;
    if (group[0]?.category !== 'streaming') return true;
    const nextGroup = array[index + 1];
    if (nextGroup?.[0]?.type !== 'text') return true;
    if (nextGroup?.[0]?.category !== 'final') return true;
    return false;
  });
};

const flattenEventGroups = (
  groups: ClientFrameworkEvent[][]
): ClientFrameworkEvent[] => {
  return groups
    .map((group) => {
      if (group[0]?.type === 'text' && group[0]?.category === 'streaming') {
        const firstItemInStream = group[0] as ClientTextEvent;
        const combinedText = group
          .map((event) => (event as ClientTextEvent).content)
          .join('');
        return [
          {
            ...firstItemInStream,
            content: combinedText,
          },
        ];
      }
      if (group[0]?.type === 'tool_status') {
        const toolStatusGroup = group as ToolStatusEvent[];
        const merged = toolStatusGroup.reduce((acc, curr) => {
          return {
            ...acc,
            ...curr,
          } as ToolStatusEvent;
        }, {} as ToolStatusEvent);
        const distinctCallIds = new Set(toolStatusGroup.map((e) => e.callId));
        merged.count = distinctCallIds.size || 1;
        return [merged as ClientFrameworkEvent];
      }
      return group;
    })
    .flat();
};

const eventToMessage = (event: ClientFrameworkEvent) => {
  if (event.type === 'text') {
    return {
      id: makeId(event),
      content: event.content,
      role: event.role || 'assistant',
      type: 'text' as const,
    } as Message;
  }

  if (event.type === 'tool_status') {
    const toolName = event.tool || 'unknown_tool';
    const input = event.input;
    const status = event.status || 'pending';
    const count = event.count;

    return {
      id: makeId(event),
      content: event.content,
      role: 'assistant' as const,
      type: 'tool' as const,
      toolName,
      input,
      status,
      count,
    } as ToolMessage;
  }

  if (event.type === 'user_input') {
    return {
      id: makeId(event),
      content: event.content,
      role: 'user' as const,
      type: 'text' as const,
      metadata: event.metadata,
      images: event.images,
    } as Message;
  }

  if (event.type === 'error') {
    return event as ErrorEvent;
  }

  if (event.type === 'interrupted') {
    return {
      id: makeId(event),
      type: 'interrupted' as const,
      role: 'system' as const,
      content: 'The response was stopped. Try again or send a new message.',
    } as InterruptedMarkerMessage;
  }

  return null;
};

/**
 * Aggregate events into messages to display.
 */
export const transformEventsToMessages = (
  events: ClientFrameworkEvent[] | null
): (Message | ToolMessage | ErrorEvent | InterruptedMarkerMessage)[] | null => {
  if (!events) return null;

  const prunedEvents = pruneInterruptedAbortSignalErrors(events);
  const displayableEvents = prunedEvents.filter((e) => isDisplayableEvent(e));
  const prunedDisplayableEvents =
    pruneInterruptedExecutingToolStatuses(displayableEvents);
  const groupedMessages = groupConsecutiveEvents(prunedDisplayableEvents);
  const prunedGroups = pruneStreamingTextGroups(groupedMessages);
  const flattenedEvents = flattenEventGroups(prunedGroups);
  return flattenedEvents.map((e) => eventToMessage(e)).filter(Boolean) as (
    | Message
    | ToolMessage
    | InterruptedMarkerMessage
  )[];
};
